import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { iframeMessageHookMap } from './hooks/iframe-messaging/IframeRegistration';
export const IframeMessageContext = createContext(null);
export const useIframeMessageContext = () => {
    const context = useContext(IframeMessageContext);
    if (!context) {
        throw new Error('useIframeMessageContext must be used within the context of the IframeMessageProvider component');
    }
    return context;
};
export const IframeMessageProvider = ({ contextRef, children }) => {
    // separate iframe state from SiteConfigState so that SiteConfigState can
    // be serialized
    const [iframe, setIframe] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);
    const iframeReadyRef = useRef(iframeReady);
    const iframeMessageHandlerMapRef = useRef({});
    const queueRef = useRef([]);
    for (let type of Object.keys(iframeMessageHookMap)) {
        const handleMessage = iframeMessageHookMap[type].handleMessage;
        // the handler is always the last item in the tuple
        iframeMessageHandlerMapRef.current[type] = handleMessage;
    }
    const postMessage = useCallback((type, body) => {
        // console.log(
        //   'postMessage',
        //   type,
        //   isIframe() ? 'iframe' : 'window',
        //   'iframeReady',
        //   iframeReady,
        //   body
        // );
        const iframeReady = iframeReadyRef.current;
        const source = isIframe() ? 'iframe' : 'window';
        const message = { source, type, body };
        if (isIframe()) {
            // send message from iframe to parent
            window.postMessage(message, '*');
        }
        else if ((iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) && iframeReady) {
            // send message from window to iframe
            iframe.contentWindow.postMessage(message, '*');
        }
        else {
            // iframe not ready so queue messages on parent window
            queueRef.current.push(message);
        }
    }, [iframe]);
    // update iframeReady reference to prevent postMessage from refreshing
    useEffect(() => void (iframeReadyRef.current = iframeReady), [iframeReady]);
    // setup iframe messaging for iframe and parent window
    useEffect(() => {
        var _a;
        // console.log('init iframe context', iframe, isIframe(), contextRef.current);
        const handleMessage = (event) => {
            if (isIframe() && event.data.source === 'iframe')
                return;
            else if (!isIframe() && event.data.source === 'window')
                return;
            const handler = iframeMessageHandlerMapRef.current[event.data.type];
            // console.log('received message', isIframe(), event.data, handler);
            if (contextRef.current) {
                handler === null || handler === void 0 ? void 0 : handler(contextRef.current, event.data);
            }
        };
        if (isIframe()) {
            window.addEventListener('message', handleMessage);
            console.log('iframe ready');
            window.postMessage({ source: 'iframe', type: 'send-iframe-ready' }, '*');
            return () => window.removeEventListener('message', handleMessage);
        }
        else {
            const handleLoad = () => {
                console.log('iframe loading');
            };
            // need to reattach the "message" event listener when iframe url changes
            setIframeReady(false);
            iframe === null || iframe === void 0 ? void 0 : iframe.addEventListener('load', handleLoad);
            (_a = iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.addEventListener('message', handleMessage);
            return () => {
                var _a;
                iframe === null || iframe === void 0 ? void 0 : iframe.removeEventListener('load', handleLoad);
                (_a = iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.removeEventListener('message', handleMessage);
            };
        }
    }, [contextRef, iframe]);
    // process queue when messages are ready to send
    useEffect(() => {
        if ((iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) && iframeReady) {
            queueRef.current.forEach(message => {
                // console.log('process queue', message);
                postMessage(message.type, message.body);
            });
            queueRef.current = [];
        }
    }, [contextRef, iframeReady, iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow, postMessage]);
    return (React.createElement(IframeMessageContext.Provider, { value: {
            iframe,
            iframeReady,
            isIframe: isIframe(),
            setIframe,
            setIframeReady,
            postMessage
        } }, children));
};
// make sure <PreviewFrame /> iframe has name set to 'preview-frame'
export function isIframe() {
    try {
        return window.self.name === 'preview-frame' && window.self !== window.top;
    }
    catch (e) {
        return true;
    }
}
//# sourceMappingURL=IframeMessageContext.js.map